import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutContent } from "@/mocks/about";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d9oov12ftmtnfcfh0bf0";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 - 樱花漫画网";
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt")?.toString().trim();
    if (honeypot) {
      setFormStatus("error");
      setFormError("提交失败，请稍后重试。");
      return;
    }
    formData.delete("website_alt");

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Object.fromEntries(
            Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
          )
        ).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络连接后重试。");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="text-center py-10 md:py-16 bg-background-100 rounded-2xl border border-background-200/70 mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于 {aboutContent.brandName}
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-sm md:text-base">
              {aboutContent.brandIntro}
            </p>
          </section>

          <section className="mb-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">我们的优势</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutContent.features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-10">
              <h2 className="text-2xl font-bold text-foreground-950 mb-6">数据一览</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {aboutContent.stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl md:text-4xl font-bold text-primary-600 mb-1">{stat.value}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">用户口碑</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutContent.testimonials.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{testimonial.name}</p>
                      <p className="text-xs text-foreground-400">{testimonial.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutContent.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-semibold text-foreground-950 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">地址</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutContent.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutContent.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电话</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutContent.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutContent.contact.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">发送消息</h2>
                <form
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>

                  <div className="honeypot-field" aria-hidden="true">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === "success" && (
                    <p className="text-sm text-green-600 text-center font-medium">
                      消息发送成功！我们会尽快与您联系。
                    </p>
                  )}
                  {formStatus === "error" && (
                    <p className="text-sm text-red-500 text-center font-medium">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                name: "樱花漫画网",
                url: window.location.origin,
                description: aboutContent.brandIntro,
                contactPoint: {
                  "@type": "ContactPoint",
                  telephone: aboutContent.contact.phone,
                  email: aboutContent.contact.email,
                  contactType: "customer service",
                },
                address: {
                  "@type": "PostalAddress",
                  streetAddress: aboutContent.contact.address,
                  addressLocality: "广州",
                  addressRegion: "广东",
                  addressCountry: "CN",
                },
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: aboutContent.faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                  },
                })),
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: window.location.origin,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "关于我们",
                  },
                ],
              }),
            }}
          />
        </div>
      </main>
      <Footer />
    </>
  );
}